/**
 * Product media gallery controller.
 *
 * The main viewer is a Flickity carousel (declarative `data-flickity` init).
 * The thumbnail rail is intentionally NOT a Flickity instance: on desktop it is a
 * native vertical scroll column (see section-main-product(-ltr).css), on mobile a
 * native horizontal scroller. This file keeps the two in sync:
 *  - clicking a thumbnail shows that media in the main viewer
 *  - changing the main slide (arrows / swipe / variant change) marks the matching
 *    thumbnail active and scrolls it into view inside the rail when it is outside
 *    the visible scroll area
 */
(function () {
  'use strict';

  function initGallery(gallery) {
    if (gallery.dataset.thumbsSynced === 'true') return;
    var viewerEl = gallery.querySelector('.product__media-list[data-flickity]');
    var thumbList = gallery.querySelector('.thumbnail-list');
    if (!viewerEl || !thumbList) return;

    var flkty = Flickity.data(viewerEl);
    if (!flkty) {
      // Options come from global.js so mobile keeps its page dots (data-flickity is desktop-only).
      var options =
        typeof window.productGalleryFlickityOptions === 'function'
          ? window.productGalleryFlickityOptions(viewerEl)
          : null;
      if (!options) {
        try {
          options = JSON.parse(viewerEl.getAttribute('data-flickity'));
        } catch (error) {
          return;
        }
      }
      flkty = new Flickity(viewerEl, options);
    }
    gallery.dataset.thumbsSynced = 'true';

    var thumbs = Array.prototype.slice.call(thumbList.querySelectorAll('.thumbnail-list__item'));

    function cellIndexFor(thumb) {
      var cells = flkty.getCellElements();
      var target = thumb.getAttribute('data-target');
      for (var i = 0; i < cells.length; i++) {
        if (cells[i].getAttribute('data-media-id') === target) return i;
      }
      // Slides and thumbnails are rendered in the same order; fall back to position.
      return thumbs.indexOf(thumb);
    }

    function scrollIntoRail(thumb) {
      var railRect = thumbList.getBoundingClientRect();
      var thumbRect = thumb.getBoundingClientRect();

      if (thumbList.scrollHeight - thumbList.clientHeight > 1) {
        var top = thumbList.scrollTop;
        if (thumbRect.top < railRect.top) {
          top += thumbRect.top - railRect.top;
        } else if (thumbRect.bottom > railRect.bottom) {
          top += thumbRect.bottom - railRect.bottom;
        }
        if (top !== thumbList.scrollTop) thumbList.scrollTo({ top: top, behavior: 'smooth' });
      }

      if (thumbList.scrollWidth - thumbList.clientWidth > 1) {
        var left = thumbList.scrollLeft;
        if (thumbRect.left < railRect.left) {
          left += thumbRect.left - railRect.left;
        } else if (thumbRect.right > railRect.right) {
          left += thumbRect.right - railRect.right;
        }
        if (left !== thumbList.scrollLeft) thumbList.scrollTo({ left: left, behavior: 'smooth' });
      }
    }

    function syncActiveThumb() {
      var cell = flkty.selectedElement;
      var mediaId = cell ? cell.getAttribute('data-media-id') : null;
      var activeThumb = null;
      thumbs.forEach(function (thumb, index) {
        var isActive = mediaId
          ? thumb.getAttribute('data-target') === mediaId
          : index === flkty.selectedIndex;
        thumb.classList.toggle('is-selected', isActive);
        var button = thumb.querySelector('.thumbnail');
        if (button) {
          if (isActive) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        }
        if (isActive) activeThumb = thumb;
      });
      if (activeThumb) scrollIntoRail(activeThumb);
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        flkty.select(cellIndexFor(thumb));
      });
    });

    flkty.on('select', syncActiveThumb);
    syncActiveThumb();
  }

  function initAll(scope) {
    if (typeof Flickity === 'undefined') return;
    (scope || document).querySelectorAll('[id^="MediaGallery-"]').forEach(initGallery);
  }

  window.initProductMediaGalleries = initAll;

  if (document.readyState === 'complete') {
    initAll();
  } else {
    // Registered after Flickity's own docReady hook (flickity.pkgd loads first),
    // so the declarative `data-flickity` viewer is already initialized here.
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
    window.addEventListener('load', function () {
      initAll();
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
